import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { get } from 'lodash';
import { ASSETS_STATUS, ASSETS_TYPES } from '../../../constants';
import Table, { Column } from '../../../modules/table';
import { Assets, Asset } from '../../../services/api/dto/Assets';
import { useAppDispatch } from '../../../store';
import { fetchAssets, setActiveTabType } from '../../../store/actions/assets';
import { RootState } from '../../../types';
import currencyFormat from '../../../utils/currencyFormat';
import { getFormatDate } from '../../../utils/utils';
import FinTokensRowComponent from '../row-component';
import './issued-tokens.scss';
import { AssetsFilters } from '../components';
import { getTokenBadgeStatus } from 'utils/assets';
import { TokenStatusBadge } from 'components';

const Number: React.FC<{ data: string }> = ({ data }) => (
  <>
    {data}
    <span> ₽</span>
  </>
);

export type AssetsType = Assets & Record<string, any>;

type Columns = Column<AssetsType>[];

const COLUMNS: Columns = [
  {
    title: 'Наименование токена',
    name: 'tokenName',
    headerStyle: {
      width: '30%',
      flexShrink: '1',
    },
    dataGetter: (data: Assets) => get(data, 'tokenName', ''),
  },
  {
    title: 'Количество',
    name: 'tokenCount',
    headerStyle: {
      width: '10%',
    },
    dataGetter: (data: Assets) => get(data, 'tokenCount', 0),
    Component: ({ rowData }: { rowData: Asset }) => {
      return (
        <div>
          <p className="p">{get(rowData, 'tokenCount', 0)}</p>
        </div>
      );
    },
  },
  {
    title: 'Сумма требований',
    name: 'number',
    Component: Number,
    headerStyle: {
      width: '15%',
    },
    dataGetter: (data: Assets) =>
      currencyFormat(get(data, 'financialClaim', 0), false),
  },
  {
    title: 'Владелец',
    name: 'issuer',
    headerStyle: {
      width: '15%',
    },
    dataGetter: (data: Assets) => get(data, 'owner', ''),
  },
  {
    title: 'Дата последней операции',
    name: 'issuedDate',
    headerStyle: {
      width: '15%',
    },
    dataGetter: (data: Assets) => {
      const date = get(data, 'initialDate', '');
      return date ? getFormatDate(new Date(date)) : '';
    },
  },
  {
    title: '',
    name: 'badge',
    headerStyle: {
      width: '15%',
      borderLeft: '0',
    },
    dataGetter: (data: Assets) => data,
    Component: ({ rowData }: { rowData: Asset }) => {
      const [statusText, variant] = getTokenBadgeStatus(rowData);
      return (
        <div className="flex justify-end">
          <TokenStatusBadge
            text={statusText}
            variant={variant}
            minWidth={180}
          />
        </div>
      );
    },
  },
];

interface IAssetsFilters {
  search: string;
  status?: ASSETS_STATUS;
  dateFrom?: Date;
  dateTo?: Date;
}

export const IssuedTokens = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { data } = useSelector((state: RootState) => state.assets);

  const [filters, setFilters] = useState<IAssetsFilters>({
    search: '',
    status: ASSETS_STATUS.ACTIVE,
    dateFrom: undefined,
    dateTo: undefined,
  });

  const onSearchTermChange = useCallback((searchTerm: string) => {
    setFilters((filters) => ({
      ...filters,
      search: searchTerm,
    }));
  }, []);

  const onSearchPeriodChange = useCallback(
    (range: { start: Date | null; end: Date | null }) => {
      setFilters((filters) => ({
        ...filters,
        dateFrom: range.start || undefined,
        dateTo: range.end || undefined,
      }));
    },
    []
  );

  const searchPeriod = useMemo(() => {
    return {
      from: filters.dateFrom || null,
      to: filters.dateTo || null,
    };
  }, [filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    dispatch(setActiveTabType({ value: ASSETS_TYPES.SIMPLE }));
  }, []);

  useEffect(() => {
    dispatch(
      fetchAssets({ type: ASSETS_TYPES.SIMPLE, forIssuer: true, ...filters })
    );
  }, [filters]);

  return (
    <>
      <div className="mb_16">
        <AssetsFilters
          onSearchTermChange={onSearchTermChange}
          onPeriodChange={onSearchPeriodChange}
          period={searchPeriod}
          onIncludeReddemChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              status: value ? undefined : ASSETS_STATUS.ACTIVE,
            }));
          }}
          includeReddem={!filters.status}
        />
      </div>
      <Table
        count={8}
        columns={COLUMNS}
        RowComponent={FinTokensRowComponent}
        onRowClick={(id: string) =>
          history.push(`/assets/details/${id}`, { id })
        }
        data={data.info}
      />
    </>
  );
};
